import { useCallback, useMemo, useRef, useState, type ReactElement, type ReactNode } from 'react';
import {
  makeStyles,
  mergeClasses,
  tokens,
  Text,
  Button,
  Spinner,
  Tooltip,
  DataGrid,
  DataGridHeader,
  DataGridHeaderCell,
  dataGridHeaderClassNames,
  dataGridRowClassNames,
  DataGridBody,
  DataGridRow,
  DataGridCell,
  type DataGridRowProps,
  createTableColumn,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  MenuDivider,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Input,
  type TableColumnDefinition,
  type TableColumnSizingOptions,
} from '@fluentui/react-components';
import {
  Home20Regular,
  ChevronLeftRegular,
  ChevronRightRegular,
  FolderAdd20Regular,
  Attach16Regular,
  MoreHorizontalRegular,
  OpenRegular,
  ArrowDownloadRegular,
  ClipboardPasteRegular,
  TabAddRegular,
  MailArrowUp20Regular,
  FolderRegular,
} from '@fluentui/react-icons';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { FolderItemIcon, MailItemIcon } from '@/components/ItemIcon';
import { UnreadBadge } from '@/components/UnreadBadge';
import { MailReadMenuItem } from '@/components/MailReadMenuItem';
import { ItemDndContext, type DragData } from '@/components/ItemDndContext';
import { CellText } from '@/components/CellText';
import { FileHereTooltip } from '@/components/FileHereTooltip';
import { useTreeStore } from '@/stores/treeStore';
import { useMailDropStore } from '@/stores/mailDropStore';
import { useFolders } from '@/hooks/useFolders';
import { useMails, useRootMails } from '@/hooks/useMails';
import { useContainers } from '@/hooks/useContainers';
import { useDeleteMail, useRenameMail } from '@/hooks/useMailMutations';
import { useSetMailRead } from '@/hooks/useSetMailRead';
import { useItemClipboard } from '@/hooks/useItemClipboard';
import { ItemActionMenuItems } from '@/components/ItemActionMenuItems';
import { pointTarget, type CursorTarget } from '@/components/cursorMenu';
import { useCreateFolder } from '@/hooks/useCreateFolder';
import { useRenameFolder, useDeleteFolder } from '@/hooks/useFolderMutations';
import { useFileMail } from '@/features/file-mail/useFileMail';
import { useFileMailToasts } from '@/features/multi-select/useFileMailToasts';
import { useSelectionStore } from '@/stores/selectionStore';
import { useRowSelection } from '@/features/multi-select/useRowSelection';
import { useMultiSelectionMenu } from '@/features/multi-select/useMultiSelectionMenu';
import {
  detailScope,
  type SelectableAttrs,
  type SelectedRef,
} from '@/features/multi-select/selection';
import { downloadMail } from '@/services/api/endpoints';
import { reportError } from '@/services/errors/errorReporter';
import { addBreadcrumb } from '@/services/breadcrumbs/breadcrumbs';
import { formatMailDate } from '@/utils/mailDate';
import { MailTargetDialog } from '@/features/tree/MailTargetDialog';
import { MoveFolderDialog } from '@/features/tree/MoveFolderDialog';
import { FolderBreadcrumb, type Crumb } from './FolderBreadcrumb';
import { ColumnSettingsPopover } from './ColumnSettingsPopover';
import {
  useMailColumnPrefs,
  MAIL_COLUMN_META,
  ALL_MAIL_COLUMNS,
  humanFileSize,
  type MailColumnId,
} from './mailColumns';
import {
  ACTIONS_COL_WIDTH,
  NAME_COL_WEIGHT,
  columnMinWidth,
  contentBudget,
  fitMiddleColumns,
  layoutFlexColumns,
} from './columnLayout';
import type { Folder, MailSummary } from '@/types/domain';

// Fondu du nom sous le bouton « Ranger ici » : un MASQUE, et non une largeur
// réduite. La boîte du libellé reste intacte, donc rien ne se déplace quand le
// bouton paraît ou disparaît, et le nom passe DERRIÈRE l'icône en s'effaçant
// plutôt que d'être coupé net.
//
// DEUX BORNES, et c'est tout l'enjeu : le fondu doit être TERMINÉ au bord gauche
// du bouton, pas le traverser. Un dégradé qui s'éteint seulement au bord droit du
// libellé (`transparent` sans position) laisse le texte à ~86 % d'opacité à
// l'aplomb de l'icône — 4 px de dégradé parcourus sur 28 — et l'icône devenait
// illisible par-dessus les lettres. Le bouton occupe les 24 derniers pixels : le
// masque est donc totalement transparent dès `100% - 24px`, et la rampe de 20 px
// qui précède évite une coupure franche. (La croix des onglets s'en tire avec une
// borne unique parce que le titre d'un onglet ne passe presque jamais dessous.)
const NAME_FADE =
  'linear-gradient(to right, #000 calc(100% - 44px), transparent calc(100% - 24px))';

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 },
  bar: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalXS}`,
    // Le conteneur de vue (`TaskPaneShell.content`) applique déjà un
    // `spacingVerticalS` AU-DESSUS de cette barre : l'espace au-dessus du fil
    // d'Ariane valait donc le double de celui du dessous. On annule cet apport
    // ici pour que les deux soient égaux — la dépendance est explicite plutôt
    // que compensée par une valeur magique.
    marginTop: `calc(-1 * ${tokens.spacingVerticalS})`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  crumbs: { flex: 1, minWidth: 0, overflow: 'hidden' },
  // Right-click anywhere in this region (grid blank space or the empty-state
  // message) offers "Nouveau dossier". Rows stop propagation so their own
  // context menu wins instead.
  body: { flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 },
  grid: {
    flex: 1,
    // `auto` et non `scroll` : dans un volet étroit, ce n'est PLUS l'ascenseur qui
    // absorbe le manque de place — les colonnes de droite s'effacent d'elles-mêmes
    // (cf. columnLayout.fitMiddleColumns), et la grille tient toujours dans le
    // volet. Il reste néanmoins deux cas où un ascenseur horizontal est légitime :
    // un volet si étroit que « Nom / Objet » ne tient pas à son plancher, et une
    // colonne qu'un utilisateur a élargie à la main (`resizableColumns`). D'où
    // `auto`, qui ne le montre alors que dans ces cas.
    overflowX: 'auto',
    overflowY: 'auto',
    minHeight: 0,
    // En-tête figé : c'est CE conteneur qui défile, donc `sticky` s'y ancre.
    // `top: 0` ne bloque que le défilement VERTICAL — l'en-tête continue de
    // suivre le défilement horizontal, ce qui est voulu : les colonnes doivent
    // rester alignées sur les cellules.
    //
    // Le fond opaque est indispensable, pas décoratif : Fluent laisse l'en-tête
    // et les lignes en fond transparent, et les lignes défileraient donc
    // VISIBLEMENT au travers de l'en-tête figé.
    [`& .${dataGridHeaderClassNames.root}`]: {
      position: 'sticky',
      top: 0,
      // Au-dessus des lignes (statiques) ET de la surbrillance de dépôt d'un mail.
      zIndex: 2,
      backgroundColor: tokens.colorNeutralBackground1,
      // Sépare l'en-tête des lignes qui passent dessous : sans ce trait, une
      // ligne à demi masquée donne l'impression d'un défaut d'affichage.
      boxShadow: `0 1px 0 0 ${tokens.colorNeutralStroke2}`,
    },
    // Plus de filets horizontaux entre les lignes : Fluent en pose un sous chaque
    // ligne, ce qui quadrillait une liste où l'alternance des icônes et le survol
    // suffisent déjà à suivre une ligne. Le SEUL trait conservé est celui qui
    // sépare l'en-tête du contenu — et il ne vient pas d'ici mais du `boxShadow`
    // ci-dessus, donc le retrait des bordures ne l'emporte pas avec lui.
    [`& .${dataGridRowClassNames.root}`]: { borderBottom: 'none' },
    // Ligne SÉLECTIONNÉE (clic simple) : même teinte que le dossier actif de
    // l'arbre, pour que « sélectionné » se lise pareil aux deux endroits.
    //
    // Écrit ici, depuis le conteneur, et non comme une classe posée sur la ligne :
    // Fluent applique son propre `:hover` (fond gris) sur la ligne avec une
    // spécificité de 0,2,0, qui l'emporterait sur une simple classe (0,1,0). La
    // sélection disparaîtrait donc sous le curseur — c'est-à-dire À L'INSTANT où
    // l'utilisateur vient de cliquer, la souris étant encore sur la ligne. Ce
    // sélecteur vaut 0,3,0 (classe du conteneur + classe de ligne + attribut) et
    // gagne dans tous les cas, survol et enfoncement compris. `appearance="brand"`
    // de Fluent souffre exactement du même défaut : sa règle de fond est en
    // spécificité 0,1,0 face au `:hover` de la même ligne.
    [`& .${dataGridRowClassNames.root}[aria-selected="true"]`]: {
      backgroundColor: tokens.colorBrandBackground2,
    },
  },
  cell: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    overflow: 'hidden',
    // Ancre du bouton « Ranger ici », posé en absolu à l'extrémité droite de la
    // colonne (cf. fileHereBtn).
    position: 'relative',
    // `width: 100%` est INDISPENSABLE, et c'est le MÊME piège que sur la cellule
    // d'actions : sans elle ce div se dimensionne sur son contenu, donc sur la
    // longueur du nom. Le bouton ancré à `right: 0` se collait alors à la fin du
    // nom — mesuré à 24 px du mot « CCRS », à 187 px du bord de la colonne — au
    // lieu de l'extrémité droite demandée. `minWidth: 0` avec, sans quoi un nom
    // trop long empêcherait le div de redescendre sous sa taille de contenu.
    width: '100%',
    minWidth: 0,
  },
  cellText: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  icon: { flexShrink: 0, color: tokens.colorNeutralForeground3 },
  unreadText: { color: tokens.colorBrandForeground1, fontWeight: tokens.fontWeightSemibold },
  dim: { color: tokens.colorNeutralForeground3 },
  empty: { flex: 1, padding: tokens.spacingVerticalXL, textAlign: 'center', color: tokens.colorNeutralForeground3 },
  // Richer empty-state: centred icon + message + action buttons.
  emptyState: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalXXL,
    textAlign: 'center',
  },
  emptyIcon: { fontSize: '48px', color: tokens.colorNeutralForeground4, marginBottom: tokens.spacingVerticalXS },
  emptyTitle: { fontWeight: tokens.fontWeightSemibold, fontSize: tokens.fontSizeBase400 },
  emptySub: { color: tokens.colorNeutralForeground3, maxWidth: '280px' },
  emptyActions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
    justifyContent: 'center',
    marginTop: tokens.spacingVerticalS,
  },
  clickRow: {
    cursor: 'pointer',
    // Le double-clic ouvre : sans ceci, il sélectionnerait AUSSI le mot sous le
    // curseur (surlignage bleu du navigateur) à chaque ouverture. Même parti que
    // l'Explorateur Windows et la liste de messages d'Outlook, où le texte d'une
    // ligne n'est pas sélectionnable à la souris.
    userSelect: 'none',
    // Révèle les actions de survol de la ligne. Sélecteur descendant plutôt
    // qu'un état React par ligne : la grille peut compter des centaines de
    // lignes, et un `useState` par ligne les ferait toutes se re-rendre au
    // passage de la souris. C'est aussi ce qui synchronise le fondu du nom
    // (`fd-name-fade`) avec l'apparition du bouton qui le recouvre — la croix
    // des onglets fait de même, mais avec un état React : là-bas il n'y a qu'une
    // poignée d'onglets, ici des centaines de lignes.
    ':hover': {
      '& .fd-row-action': { opacity: 1, pointerEvents: 'auto' },
      '& .fd-name-fade': { maskImage: NAME_FADE, WebkitMaskImage: NAME_FADE },
    },
    // Au clavier, le bouton doit apparaître dès qu'il prend le focus, sinon il
    // reste inatteignable sans souris (même piège que le menu ⋯ de l'arbre,
    // corrigé en `opacity` plutôt qu'en `display: none`).
    ':focus-within': {
      '& .fd-row-action': { opacity: 1, pointerEvents: 'auto' },
      '& .fd-name-fade': { maskImage: NAME_FADE, WebkitMaskImage: NAME_FADE },
    },
  },
  // Bouton « Ranger ici » d'une ligne de DOSSIER, à l'extrémité droite de la
  // colonne « Nom / Objet » — et non plus dans la colonne d'actions, à l'autre
  // bout de la ligne : c'est au bout du nom du dossier qu'on lit sa cible, pas
  // après trois colonnes vides.
  //
  // EN ABSOLU, donc HORS FLUX, exactement comme la croix de fermeture d'un
  // onglet : le bouton n'occupe aucune place, si bien que son apparition au
  // survol ne peut ni élargir la cellule ni décaler le nom, et qu'aucun vide
  // n'est réservé quand il est masqué. C'est le nom qui cède la place, en
  // s'estompant dessous (cf. nameFade / NAME_FADE).
  fileHereBtn: {
    position: 'absolute',
    right: 0,
    // Centré sans dépendre de la hauteur de ligne (34 px en densité `small`,
    // mais elle vient du thème).
    top: '50%',
    transform: 'translateY(-50%)',
    minWidth: '24px',
    width: '24px',
    height: '24px',
    padding: 0,
  },
  // Le nom d'un dossier doit s'étirer jusqu'au BORD DROIT de la colonne, sinon le
  // fondu tomberait à la fin du texte : un dossier au nom court verrait ses
  // dernières lettres s'effacer alors que le bouton est à 200 px de là. Réservé
  // aux lignes de dossier — sur un mail, `flex: 1` repousserait le trombone à
  // l'autre bout de la colonne au lieu de le laisser coller à l'objet.
  nameGrow: { flex: 1, minWidth: 0 },
  rowHoverAction: {
    opacity: 0,
    pointerEvents: 'none',
    transition: 'opacity 80ms ease-out',
    ':focus-visible': { opacity: 1, pointerEvents: 'auto' },
    // Un déclencheur de menu OUVERT reste visible même si la souris a quitté la
    // ligne : sans cette règle, ouvrir le ⋯ puis glisser la souris vers le menu
    // faisait disparaître le bouton sous le menu qu'il vient d'ouvrir.
    // `:focus-within` de la ligne n'y suffit pas — Fluent monte le menu dans un
    // portail, donc hors du sous-arbre DOM de la ligne.
    '&[aria-expanded="true"]': { opacity: 1, pointerEvents: 'auto' },
  },
  // Infobulle riche du bouton « Ranger ici » — mêmes styles que FolderRow dans
  // l'arbre, pour que le même geste donne exactement le même rendu aux deux
  // endroits.
  tip: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    maxWidth: '260px',
  },
  // Le trombone porte une infobulle, donc il doit être atteignable au clavier :
  // un span focusable, faute de quoi les noms de pièces jointes ne seraient
  // accessibles qu'à la souris.
  attachTrigger: {
    display: 'inline-flex',
    alignItems: 'center',
    flexShrink: 0,
    ':focus-visible': {
      outline: `1px solid ${tokens.colorStrokeFocus2}`,
      borderRadius: tokens.borderRadiusSmall,
    },
  },
  tipTitle: { fontWeight: tokens.fontWeightSemibold },
  tipFrom: { fontWeight: tokens.fontWeightSemibold },
  tipSubject: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  tipDate: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  // Mail-drop target highlight on a subfolder row — mirrors the tree's
  // FolderRow.rowDropOver so a dragged mail lights up the hovered folder.
  rowDropOver: {
    outline: `2px dashed ${tokens.colorBrandStroke1}`,
    outlineOffset: '-2px',
    backgroundColor: tokens.colorBrandBackground2,
  },
  // Ligne en cours de déplacement. Atténuée SANS être translatée, contrairement à
  // `DraggableFolder` dans l'arbre : décaler une ligne de grille la ferait
  // chevaucher ses voisines et fausserait la lecture de la liste. C'est la
  // vignette qui suit le curseur (DragOverlay de ItemDndContext) qui montre ce
  // qu'on transporte ; la ligne d'origine n'a qu'à dire « c'est moi qui pars ».
  rowDragging: { opacity: 0.5 },
});

type Row =
  | { kind: 'folder'; id: string; folder: Folder }
  | { kind: 'mail'; id: string; mail: MailSummary };

/**
 * `DataGridRow` avec sa `ref`, dont dnd-kit a besoin pour mesurer la ligne.
 *
 * Fluent l'expose sous DEUX signatures qui s'excluent : la générique
 * (`DataGridRow<Row>`), qui type correctement `renderCell` mais **perd la ref**, et
 * celle de `ForwardRefComponent`, qui garde la ref mais rend `renderCell` non
 * typé. Cet alias reprend les deux — un `as` unique et localisé valait mieux que
 * renoncer au typage des cellules ou que d'encapsuler la ligne dans un `<div>`,
 * qui casserait la structure de tableau (et, en `display: contents`, priverait
 * dnd-kit de toute boîte à mesurer).
 */
const RowWithRef = DataGridRow as unknown as (
  props: DataGridRowProps<Row> & { ref?: (node: HTMLElement | null) => void } & Record<string, unknown>,
) => ReactElement;

/**
 * Ce qu'une ligne représente pour la MULTI-SÉLECTION.
 *
 * L'identifiant est celui de l'élément (mail ou dossier), et NON le `row.id` de la
 * grille (préfixé `m:` / `f:`) : la sélection est partagée avec l'arbre, où ces
 * préfixes n'existent pas, et une même ligne doit s'y reconnaître.
 */
function refOfRow(row: Row): SelectedRef {
  return row.kind === 'folder'
    ? {
        kind: 'folder',
        id: row.folder.id,
        containerId: row.folder.containerId,
        parentId: row.folder.parentId,
        label: row.folder.name,
      }
    : {
        kind: 'mail',
        id: row.mail.id,
        containerId: row.mail.containerId,
        parentId: row.mail.folderId,
        label: (row.mail.displayName || row.mail.subject || '(sans objet)').trim(),
      };
}

/** Ce qu'une ligne traîne, dans le contrat partagé avec l'arbre. */
function dragDataOf(row: Row): DragData {
  return row.kind === 'folder'
    ? {
        type: 'folder',
        folderId: row.folder.id,
        containerId: row.folder.containerId,
        parentId: row.folder.parentId,
      }
    : {
        type: 'mail',
        mailId: row.mail.id,
        // `null` pour un mail rangé à la racine du projet — cas propre à cette
        // page, l'arbre ne montrant les mails que sous un dossier.
        folderId: row.mail.folderId,
        containerId: row.mail.containerId,
      };
}

/**
 * Une ligne de la grille : sélectionnable, ouvrable au double-clic, et
 * **déplaçable** vers un dossier listé dans la même page.
 *
 * Composant à part, et il DOIT l'être : `useDraggable` / `useDroppable` sont des
 * hooks, or la fonction de rendu de `DataGridBody` est appelée une fois par ligne
 * à l'intérieur du rendu de Fluent. Y appeler des hooks les rattacherait au
 * composant parent, dans un ordre qui changerait avec le nombre de lignes — ce que
 * React interdit.
 */
function DetailGridRow({
  item,
  selected,
  officeDropOver,
  attrs,
  onSelect,
  onOpen,
  onContext,
}: {
  item: Row;
  selected: boolean;
  /** Survol par un mail traîné depuis OUTLOOK (chemin Office, cf. mailDropStore). */
  officeDropOver: boolean;
  /** Attributs de multi-sélection : c'est par eux que l'ordre visuel se relit. */
  attrs: SelectableAttrs;
  onSelect: (e: React.MouseEvent) => void;
  onOpen: () => void;
  onContext: (e: React.MouseEvent) => void;
}) {
  const styles = useStyles();
  const isFolder = item.kind === 'folder';
  const data = dragDataOf(item);

  const { setNodeRef: setDragRef, listeners, isDragging } = useDraggable({
    id: `detail-drag-${item.id}`,
    data,
  });
  // Seules les lignes de DOSSIER acceptent un dépôt : un mail n'en contient pas.
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `detail-drop-${item.id}`,
    disabled: !isFolder,
    data,
  });
  const setRef = (node: HTMLElement | null) => {
    setDragRef(node);
    setDropRef(node);
  };

  return (
    <RowWithRef
      ref={setRef}
      // Les écouteurs de dnd-kit SEULEMENT, jamais ses `attributes` : ceux-ci
      // posent `role="button"` et `tabIndex`, qui écraseraient le `role="row"` de
      // la grille et casseraient sa sémantique de tableau. Conséquence assumée,
      // identique à celle de l'arbre : le déplacement se fait à la souris, pas au
      // clavier (le presse-papiers Couper/Coller, lui, est au clavier partout).
      {...listeners}
      // Tag folder rows so the Office drag hit-test
      // (dropHitTest) can resolve which subfolder is under the
      // cursor and file/highlight there.
      data-drop-folder-id={isFolder ? item.folder.id : undefined}
      // Porte la sélection au clavier autant qu'à l'écran : posé
      // sur TOUTES les lignes (donc « false » pour les autres),
      // il annonce une liste sélectionnable et pas une seule ligne
      // à l'état bizarre. C'est aussi lui qui déclenche le fond
      // coloré, cf. la règle `[aria-selected="true"]` de `grid`.
      aria-selected={selected}
      {...attrs}
      className={mergeClasses(
        styles.clickRow,
        // Même surbrillance pour les deux provenances — un mail traîné depuis
        // Outlook et un élément traîné depuis la page visent la même cible et
        // doivent le montrer pareil. `!isDragging` évite qu'un dossier se
        // désigne LUI-MÊME comme cible pendant qu'on le traîne.
        (officeDropOver || (isOver && !isDragging)) && styles.rowDropOver,
        isDragging && styles.rowDragging,
      )}
      onClick={onSelect}
      onDoubleClick={onOpen}
      onContextMenu={onContext}
    >
      {({ renderCell }) => <DataGridCell>{renderCell(item)}</DataGridCell>}
    </RowWithRef>
  );
}

// Where the shared context menu was invoked and on what (a row, or the blank
// area → null). Drives both the ⋯ button and right-click.
//
// `CursorTarget` / `pointTarget` vivaient ici ; ils sont désormais partagés avec
// les menus de l'arbre de la page principale, qui s'ancraient encore sur leur
// bouton ⋯ — cf. @/components/cursorMenu.
interface CtxState {
  target: CursorTarget;
  row: Row | null;
}

function mailLabel(m: MailSummary): string {
  return (m.displayName || m.subject || '(sans objet)').trim() || '(sans objet)';
}

function mailFilename(name: string): string {
  return `${name.replace(/[\\/:*?"<>|\r\n\t]/g, '_').slice(0, 150) || 'message'}.msg`;
}

// Sort keys per toggleable column. Folder rows have no mail metadata → they sort
// with an empty / negative key (ahead of mails), matching the name column which
// already places folders first.
function fromText(r: Row): string {
  return r.kind === 'mail' ? r.mail.fromName || r.mail.fromAddress || '' : '';
}
/** Nom ET adresse de l'expéditeur, pour l'infobulle de la colonne « De ». */
function senderTooltip(m: MailSummary): string {
  const name = m.fromName?.trim();
  const address = m.fromAddress?.trim();
  if (name && address) return `${name} · ${address}`;
  return name || address || '';
}

/**
 * Infobulle du trombone : les noms des pièces jointes, extension comprise.
 *
 * `attachmentNames` à `null` veut dire « jamais extrait » — mail rangé avant que
 * la colonne existe et pas encore rattrapé, ou .msg illisible — et NON « aucune ».
 * On s'en tient alors au libellé générique : mieux vaut ne rien préciser que
 * laisser croire à une liste vide. Idem pour une liste vide sur un mail que
 * `hasAttachments` dit pourtant porteur de pièces jointes (état incohérent).
 */
// `ReactElement | string` et non `ReactNode` : le slot `content` de Fluent
// n'accepte ni `undefined` ni `bigint`, que ReactNode inclut tous les deux.
function attachmentTooltip(
  m: MailSummary,
  styles: ReturnType<typeof useStyles>,
): ReactElement | string {
  const names = m.attachmentNames;
  if (!names || names.length === 0) return 'Pièces jointes';

  return (
    <div className={styles.tip}>
      <span className={styles.tipTitle}>
        {names.length === 1 ? '1 pièce jointe' : `${names.length} pièces jointes`}
      </span>
      {/* Deux pièces jointes peuvent porter le même nom : l'index entre dans la
          clé, sinon React les confondrait. */}
      {names.map((name, i) => (
        <span key={`${name}-${i}`} className={styles.tipSubject}>
          {name}
        </span>
      ))}
    </div>
  );
}
function dateText(r: Row): string {
  return r.kind === 'mail' ? (r.mail.receivedAt ?? r.mail.sentAt ?? '') : '';
}
function sentText(r: Row): string {
  return r.kind === 'mail' ? (r.mail.sentAt ?? '') : '';
}
function sizeVal(r: Row): number {
  return r.kind === 'mail' ? (r.mail.sizeBytes ?? 0) : -1;
}
function filedByText(r: Row): string {
  return r.kind === 'mail' ? (r.mail.filedByName ?? '') : '';
}
function filedAtText(r: Row): string {
  return r.kind === 'mail' ? r.mail.filedAt : '';
}

const HEADER_DND_TYPE = 'application/x-prism-mail-col';

// Wraps a toggleable column's header label to make it draggable so the user can
// reorder columns directly in the grid (not only via the settings popover). A
// custom dataTransfer type keeps this isolated from the mail-file drop (which
// uses the 'Files' type) and from Fluent's own column-resize handle. A plain
// click still bubbles to the header's sort button; only a drag reorders.
function ReorderableHeader({
  columnId,
  children,
}: {
  columnId: MailColumnId;
  children: ReactNode;
}) {
  const reorder = useMailColumnPrefs((s) => s.reorder);
  const [over, setOver] = useState(false);
  return (
    <span
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData(HEADER_DND_TYPE, columnId);
        e.dataTransfer.effectAllowed = 'move';
      }}
      onDragOver={(e) => {
        if (!e.dataTransfer.types.includes(HEADER_DND_TYPE)) return;
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = 'move';
        if (!over) setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        if (!e.dataTransfer.types.includes(HEADER_DND_TYPE)) return;
        e.preventDefault();
        e.stopPropagation();
        setOver(false);
        const activeId = e.dataTransfer.getData(HEADER_DND_TYPE) as MailColumnId;
        if (activeId && activeId !== columnId) reorder(activeId, columnId);
      }}
      style={{
        cursor: 'grab',
        display: 'inline-flex',
        alignItems: 'center',
        padding: '0 2px',
        boxShadow: over ? `inset 0 -2px 0 0 ${tokens.colorBrandStroke1}` : undefined,
      }}
      title="Glisser pour réordonner la colonne"
    >
      {children}
    </span>
  );
}

function isReorderableColumn(id: unknown): id is MailColumnId {
  return typeof id === 'string' && (ALL_MAIL_COLUMNS as string[]).includes(id);
}

// Largeurs de colonnes : tout le calcul vit dans columnLayout.ts, y compris le
// choix des colonnes que le volet peut porter (cf. l'en-tête de ce module pour la
// raison — un seuil et un calcul séparés finiraient par se contredire).

export function FolderDetail() {
  const styles = useStyles();
  const containerId = useTreeStore((s) => s.activeContainerId);
  const detailFolderId = useTreeStore((s) => s.detailFolderId);
  const openFolderDetail = useTreeStore((s) => s.openFolderDetail);
  const openDetailTab = useTreeStore((s) => s.openDetailTab);
  const backToTree = useTreeStore((s) => s.backToTree);
  const goBack = useTreeStore((s) => s.goBack);
  const goForward = useTreeStore((s) => s.goForward);
  const canGoBack = useTreeStore((s) => s.canGoBack);
  const canGoForward = useTreeStore((s) => s.canGoForward);
  // Which subfolder row (if any) a dragged mail is currently hovering, so it
  // lights up. Driven by the Office drag hit-test (data-drop-folder-id below).
  const dropTarget = useMailDropStore((s) => s.target);

  const { data: containers } = useContainers();
  const { data: folders, isPending: foldersLoading } = useFolders(containerId);
  // A folder view lists that folder's mails; the container-ROOT view
  // (detailFolderId null, reached by double-clicking a project) lists the
  // project's root mails (folder_id null). Exactly one query is enabled.
  const atRoot = detailFolderId === null;
  const { data: folderMails, isPending: folderMailsLoading } = useMails(detailFolderId);
  const { data: rootMails, isPending: rootMailsLoading } = useRootMails(atRoot ? containerId : null);
  const mails = atRoot ? rootMails : folderMails;
  const mailsLoading = atRoot ? rootMailsLoading : folderMailsLoading;
  const mailsEnabled = !atRoot || Boolean(containerId);

  const delMail = useDeleteMail();
  const renameMail = useRenameMail();
  const setRead = useSetMailRead();
  const { canPaste, pending, isBusy, copyMails, cutMails, copyFolder, cutFolder, pasteInto } =
    useItemClipboard();
  const createFolderMut = useCreateFolder();
  const renameFolderMut = useRenameFolder();
  const deleteFolderMut = useDeleteFolder();
  // Current Outlook selection (for the empty-state "Ranger ici" button) + the
  // filing mutation and its toasts, mirroring the tree's ContainerSection.
  const selection = useSelectionStore((s) => s.items);
  const fileMailMut = useFileMail();
  const { showLoading, showResults, showError } = useFileMailToasts();
  // Mails whose .msg is currently being fetched for download — drives a per-row
  // spinner so a left-click is visibly acknowledged until the file lands.
  const [downloadingIds, setDownloadingIds] = useState<Set<string>>(new Set());

  // LA SÉLECTION DE LIGNES, désormais MULTIPLE et partagée avec l'arbre.
  //
  // Remplace un `selectedRowId` local, purement décoratif : la sélection porte
  // maintenant des actions (déplacer, supprimer, marquer lu), donc elle doit dire
  // exactement QUOI est visé — la nature de chaque élément, son espace, son
  // parent — et pas seulement un identifiant de ligne. Un clic simple continue de
  // n'en sélectionner qu'une, CTRL ajoute, MAJ étend.
  const selScope = detailScope(containerId ?? 'none', detailFolderId ?? null);
  const sel = useRowSelection(selScope);
  const multiMenu = useMultiSelectionMenu({ items: sel.items, onDone: () => setCtx(null) });

  // Shared context menu + the dialogs its items open.
  const [ctx, setCtx] = useState<CtxState | null>(null);
  const [createParentId, setCreateParentId] = useState<string | null | undefined>(undefined);
  const [createName, setCreateName] = useState('');
  const [mailRename, setMailRename] = useState<MailSummary | null>(null);
  const [mailRenameVal, setMailRenameVal] = useState('');
  const [mailDelete, setMailDelete] = useState<MailSummary | null>(null);
  const [mailTarget, setMailTarget] = useState<{ mode: 'copy' | 'move'; mail: MailSummary } | null>(null);
  const [folderRename, setFolderRename] = useState<Folder | null>(null);
  const [folderRenameVal, setFolderRenameVal] = useState('');
  const [folderDelete, setFolderDelete] = useState<Folder | null>(null);
  // Un seul état pour le dialogue de destination d'un dossier : il sert au
  // déplacement ET à la copie, comme MailTargetDialog le fait pour les mails.
  const [folderTarget, setFolderTarget] = useState<{
    mode: 'copy' | 'move';
    folder: Folder;
  } | null>(null);

  // Infobulle de « Coller » : dire CE QUI sera collé évite d'avoir à s'en
  // souvenir, le presse-papiers étant partagé entre mails et dossiers.
  const pasteHint =
    pending === 'folder'
      ? 'Coller le dossier copié dans ce dossier'
      : pending === 'mails'
        ? 'Coller les mails copiés dans ce dossier'
        : undefined;

  const container = containers?.find((c) => c.id === containerId) ?? null;
  const byId = useMemo(() => new Map((folders ?? []).map((f) => [f.id, f])), [folders]);

  // Ancestor chain (root → current) for the breadcrumb.
  const chain = useMemo(() => {
    const out: Folder[] = [];
    let cursor: string | null = detailFolderId;
    const seen = new Set<string>();
    while (cursor && !seen.has(cursor)) {
      const f = byId.get(cursor);
      if (!f) break;
      out.unshift(f);
      seen.add(cursor);
      cursor = f.parentId;
    }
    return out;
  }, [detailFolderId, byId]);

  // Breadcrumb segments: project root → each ancestor → current folder. The
  // FolderBreadcrumb collapses the middle into a "…" menu when the pane is too
  // narrow to show them all.
  const crumbs = useMemo<Crumb[]>(
    () => [
      {
        id: '__root__',
        name: container?.name ?? 'Projet',
        current: !detailFolderId,
        onClick: () => containerId && openFolderDetail(containerId, null),
      },
      ...chain.map((f) => ({
        id: f.id,
        name: f.name,
        current: f.id === detailFolderId,
        onClick: () => containerId && openFolderDetail(containerId, f.id),
      })),
    ],
    [container?.name, chain, detailFolderId, containerId, openFolderDetail],
  );

  // Direct children of the current folder (or roots when at the container root).
  const subfolders = useMemo(() => {
    return (folders ?? [])
      .filter((f) => (f.parentId ?? null) === (detailFolderId ?? null))
      .sort((a, b) => a.position - b.position || a.name.localeCompare(b.name));
  }, [folders, detailFolderId]);


  const rows: Row[] = useMemo(() => {
    const folderRows: Row[] = subfolders.map((f) => ({ kind: 'folder', id: `f:${f.id}`, folder: f }));
    const mailRows: Row[] = (mails ?? []).map((m) => ({ kind: 'mail', id: `m:${m.id}`, mail: m }));
    return [...folderRows, ...mailRows];
  }, [subfolders, mails]);

  const openMail = async (m: MailSummary) => {
    // Ignore repeat clicks while this mail is already being fetched — otherwise
    // a second click fires another download of the same file.
    if (downloadingIds.has(m.id)) return;
    setDownloadingIds((prev) => new Set(prev).add(m.id));
    // Marqué à l'OUVERTURE, et non au succès du téléchargement : c'est le geste de
    // l'utilisateur qui définit « je l'ai vu ». Le lier au succès laisserait un
    // mail ouvert vingt fois en « non lu » si le stockage bronche, et l'appel
    // serveur est de toute façon idempotent.
    setRead(m.id, true);
    try {
      const blob = await downloadMail(m.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = mailFilename(mailLabel(m));
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (err) {
      reportError({ error: err instanceof Error ? err : new Error(String(err)), tags: ['mail', 'open'] });
    } finally {
      // Cleared once the blob is ready and the download has been triggered (the
      // file now appears in the browser's downloads).
      setDownloadingIds((prev) => {
        const next = new Set(prev);
        next.delete(m.id);
        return next;
      });
    }
  };

  /**
   * Ouvre une ligne : entrer dans le dossier, ou télécharger le mail.
   *
   * Appelé au DOUBLE-clic ; le clic simple ne fait plus que sélectionner. C'est la
   * convention de l'Explorateur, celle de l'arbre de la page d'accueil (où le
   * double-clic sur un espace ou un dossier ouvre déjà cette page) et surtout
   * l'attente naturelle ici : viser une ligne pour la désigner — pour lire son
   * expéditeur, ouvrir son menu, la comparer à une autre — ne doit pas la faire
   * partir en navigation ni lancer le téléchargement d'un .msg.
   */
  const openRow = (row: Row) => {
    if (row.kind === 'folder') {
      addBreadcrumb({ category: 'ui.click', message: 'detail-open-folder', data: { id: row.folder.id } });
      if (containerId) openFolderDetail(containerId, row.folder.id);
    } else {
      void openMail(row.mail);
    }
  };

  // --- Shared context menu wiring -----------------------------------------
  const openRowMenuAt = (x: number, y: number, row: Row) => setCtx({ target: pointTarget(x, y), row });
  const onEmptyContext = (e: React.MouseEvent) => {
    // Only fires when the right-click wasn't on a row (rows stopPropagation).
    e.preventDefault();
    setCtx({ target: pointTarget(e.clientX, e.clientY), row: null });
  };
  const closeCtx = () => setCtx(null);

  const startCreate = (parentId: string | null) => {
    closeCtx();
    setCreateName('');
    setCreateParentId(parentId);
  };
  const submitCreate = () => {
    const name = createName.trim();
    // Empty name is a no-op (keep the dialog open) — matches the disabled
    // "Créer" button. Only an explicit Annuler / dismiss closes the dialog.
    if (!name) return;
    if (!containerId || createParentId === undefined) {
      setCreateParentId(undefined);
      return;
    }
    createFolderMut.mutate(
      { containerId, parentId: createParentId, name },
      {
        // Close ONLY on success. `onSettled` also fired on failure, so a refused
        // name (duplicate) dismissed the dialog and dropped what the user had
        // typed. Leaving it open lets them fix the name in place — the reason for
        // the refusal arrives as a toast from useCreateFolder.
        onSuccess: () => {
          setCreateParentId(undefined);
          setCreateName('');
        },
      },
    );
  };

  /**
   * Range la sélection Outlook dans un dossier PRÉCIS.
   *
   * Sert trois points d'entrée : le raccourci de l'état vide (dossier affiché),
   * l'entrée « Ranger N mail(s) ici » du menu contextuel d'une ligne, et le bouton
   * qui apparaît au survol d'une ligne de dossier — les trois manquaient sur cette
   * page alors qu'ils existaient dans l'arbre.
   */
  const fileSelectionInto = (folderId: string) => {
    if (selection.length === 0) return;
    // Sans projet actif il n'y aurait ni arbre ni dossier à viser : les trois
    // points d'entrée sont tous rendus sous un `containerId`.
    if (!containerId) return;
    const target = folderId;
    const toastId = showLoading(selection.length);
    fileMailMut.mutate(
      { targetFolderId: target, targetContainerId: containerId, items: selection },
      {
        onSuccess: (results) => showResults(results, toastId),
        onError: (err) => showError(err instanceof Error ? err.message : String(err), toastId),
      },
    );
  };

  const startMailRename = (m: MailSummary) => {
    closeCtx();
    setMailRenameVal(m.displayName ?? m.subject ?? '');
    setMailRename(m);
  };
  const submitMailRename = () => {
    if (!mailRename) return;
    renameMail.mutate(
      { mailId: mailRename.id, folderId: mailRename.folderId, displayName: mailRenameVal.trim() },
      { onSettled: () => setMailRename(null) },
    );
  };

  const startFolderRename = (f: Folder) => {
    closeCtx();
    setFolderRenameVal(f.name);
    setFolderRename(f);
  };
  const submitFolderRename = () => {
    if (!folderRename) return;
    const name = folderRenameVal.trim();
    if (!name || name === folderRename.name) {
      setFolderRename(null);
      return;
    }
    renameFolderMut.mutate(
      { folderId: folderRename.id, name },
      { onSettled: () => setFolderRename(null) },
    );
  };

  // Menu contents per row kind. Taking the concrete Folder/MailSummary as an
  // argument keeps type-narrowing out of the onClick closures (TS drops
  // narrowing of a mutable `ctx.row` access inside a closure).
  //
  // La FIN des deux menus vient de `ItemActionMenuItems` : mêmes actions, mêmes
  // libellés, mêmes icônes, même ordre que dans l'arbre. Seule la tête — ce qui
  // est propre à un dossier ou à un mail — est écrite ici.
  const folderMenuItems = (folder: Folder) => (
    <>
      {selection.length > 0 && (
        <>
          <MenuItem
            icon={<MailArrowUp20Regular />}
            onClick={() => {
              closeCtx();
              fileSelectionInto(folder.id);
            }}
          >
            Ranger {selection.length} mail(s) ici
          </MenuItem>
          <MenuDivider />
        </>
      )}
      <MenuItem
        icon={<OpenRegular />}
        onClick={() => {
          closeCtx();
          if (containerId) openFolderDetail(containerId, folder.id);
        }}
      >
        Ouvrir
      </MenuItem>
      <MenuItem
        icon={<TabAddRegular />}
        onClick={() => {
          closeCtx();
          if (containerId) openDetailTab(containerId, folder.id);
        }}
      >
        Ouvrir dans un nouvel onglet
      </MenuItem>
      <MenuItem icon={<FolderAdd20Regular />} onClick={() => startCreate(folder.id)}>
        Nouveau sous-dossier
      </MenuItem>
      <ItemActionMenuItems
        onRename={() => startFolderRename(folder)}
        onCopy={() => {
          closeCtx();
          copyFolder({
            folderId: folder.id,
            containerId: folder.containerId,
            parentId: folder.parentId,
            name: folder.name,
          });
        }}
        onCut={() => {
          closeCtx();
          cutFolder({
            folderId: folder.id,
            containerId: folder.containerId,
            parentId: folder.parentId,
            name: folder.name,
          });
        }}
        onPaste={() => {
          closeCtx();
          pasteInto(folder.id, folder.containerId);
        }}
        pasteDisabled={!canPaste || isBusy}
        pasteHint={pasteHint}
        onCopyTo={() => {
          closeCtx();
          setFolderTarget({ mode: 'copy', folder });
        }}
        onMoveTo={() => {
          closeCtx();
          setFolderTarget({ mode: 'move', folder });
        }}
        onDelete={() => {
          closeCtx();
          setFolderDelete(folder);
        }}
      />
    </>
  );

  const mailMenuItems = (mail: MailSummary) => (
    <>
      <MenuItem
        icon={<ArrowDownloadRegular />}
        onClick={() => {
          closeCtx();
          void openMail(mail);
        }}
      >
        Ouvrir / Télécharger
      </MenuItem>
      {/* Juste après « Ouvrir », et AVANT le bloc partagé : les deux agissent sur
          le mail en tant que message, alors que la suite (renommer,
          presse-papiers, supprimer) agit sur la fiche rangée. */}
      <MailReadMenuItem mailId={mail.id} isRead={mail.isRead} onDone={closeCtx} />
      <ItemActionMenuItems
        onRename={() => startMailRename(mail)}
        onCopy={() => {
          closeCtx();
          copyMails([
            {
              mailId: mail.id,
              folderId: mail.folderId,
              containerId: mail.containerId,
              label: mailLabel(mail),
            },
          ]);
        }}
        onCut={() => {
          closeCtx();
          cutMails([
            {
              mailId: mail.id,
              folderId: mail.folderId,
              containerId: mail.containerId,
              label: mailLabel(mail),
            },
          ]);
        }}
        onPaste={() => {
          closeCtx();
          // Coller « sur un mail » colle dans le dossier qui le contient. À la
          // racine d'un projet il n'y a pas de dossier cible : l'action est
          // désactivée plutôt que d'échouer côté serveur.
          if (mail.folderId) pasteInto(mail.folderId, mail.containerId);
        }}
        pasteDisabled={!canPaste || isBusy || mail.folderId === null}
        pasteHint={pasteHint}
        onCopyTo={() => {
          closeCtx();
          setMailTarget({ mode: 'copy', mail });
        }}
        onMoveTo={() => {
          closeCtx();
          setMailTarget({ mode: 'move', mail });
        }}
        onDelete={() => {
          closeCtx();
          setMailDelete(mail);
        }}
      />
    </>
  );

  const ctxRow = ctx?.row ?? null;

  // Column show/hide + order (persisted, shared with the header drag and the
  // settings popover). `name` is always first, `actions` always last; the
  // middle columns come from the user's prefs.
  const columnOrder = useMailColumnPrefs((s) => s.order);
  const columnHidden = useMailColumnPrefs((s) => s.hidden);
  const visibleMiddle = columnOrder.filter((id) => !columnHidden.includes(id));

  // Measure the grid viewport so columns fill the pane width proportionally
  // (responsive). contentRect excludes the vertical scrollbar, so the computed
  // widths never trigger a horizontal one.
  const [gridWidth, setGridWidth] = useState(0);
  const gridResizeObs = useRef<ResizeObserver | null>(null);
  const gridRef = useCallback((node: HTMLDivElement | null) => {
    gridResizeObs.current?.disconnect();
    if (!node) return;
    // Mesure IMMÉDIATE, avant même d'installer l'observateur : sans elle, le
    // premier rendu se fait à `gridWidth = 0` et n'obtient ses vraies largeurs
    // qu'au premier appel du ResizeObserver. Les colonnes sautaient donc de leur
    // largeur naturelle à leur largeur calculée juste après l'affichage — et
    // surtout, tout hôte où l'observateur ne se déclenche pas restait
    // définitivement sur le repli (constaté au banc d'essai : `ResizeObserver`
    // existe mais n'appelle jamais son écouteur quand le volet ne compose pas).
    setGridWidth(Math.round(node.getBoundingClientRect().width));
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[entries.length - 1]?.contentRect.width ?? 0;
      setGridWidth((prev) => (Math.abs(prev - w) >= 1 ? Math.round(w) : prev));
    });
    ro.observe(node);
    gridResizeObs.current = ro;
  }, []);

  // Colonnes que le volet peut RÉELLEMENT porter. Dans un volet étroit, la
  // dernière colonne s'efface au lieu de faire apparaître un ascenseur horizontal :
  // un volet Outlook de 340 px affichait « De » et « Envoyé le » à leur largeur
  // minimale, « Nom / Objet » rabattue à son plancher de 140 px, ET un ascenseur
  // pour les pixels manquants — noms tronqués et défilement à la fois, alors que
  // l'objet du mail est justement l'information qu'on lit en premier.
  //
  // Mesuré ici et pas au style près : la décision dépend de la largeur en pixels,
  // qu'aucune requête média ne connaît (le volet n'est pas la fenêtre). D'où le
  // ResizeObserver ci-dessus, remonté avant la construction des colonnes puisque
  // c'est lui qui décide desquelles existent.
  const visibleKey = visibleMiddle.join(',');
  const fittedMiddle = useMemo(
    () => fitMiddleColumns(gridWidth, visibleMiddle),
    // visibleKey encode visibleMiddle : on ne recalcule qu'au redimensionnement du
    // volet ou au basculement d'une colonne.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [gridWidth, visibleKey],
  );
  const fittedKey = fittedMiddle.join(',');
  // Colonnes demandées par l'utilisateur mais que le volet ne peut pas porter. Le
  // sélecteur de colonnes les montre cochées : sans cette indication, il
  // AFFIRMERAIT quelque chose de faux, et chercher pourquoi « Envoyé le » ne
  // paraît pas mènerait à la décocher puis la recocher sans rien voir changer.
  const narrowHidden = visibleMiddle.slice(fittedMiddle.length);

  // Per-id factory for the toggleable middle columns.
  const buildMiddleColumn = (id: MailColumnId): TableColumnDefinition<Row> => {
    switch (id) {
      // Toutes les cellules passent par CellText : dans un volet étroit aux
      // colonnes redimensionnables, chacune de ces valeurs finit tôt ou tard
      // coupée, et l'ellipse ne dit pas ce qu'elle cache. L'infobulle n'apparaît
      // que si le texte est réellement tronqué (cf. CellText).
      case 'from':
        return createTableColumn<Row>({
          columnId: 'from',
          compare: (a, b) => fromText(a).localeCompare(fromText(b)),
          renderHeaderCell: () => MAIL_COLUMN_META.from.label,
          renderCell: (row) => (
            <CellText
              className={styles.cellText}
              text={fromText(row)}
              // L'adresse complète en plus du nom : c'est elle qui départage deux
              // homonymes, et la colonne est trop étroite pour l'afficher.
              tooltip={row.kind === 'mail' ? senderTooltip(row.mail) : null}
            />
          ),
        });
      case 'date':
        return createTableColumn<Row>({
          columnId: 'date',
          compare: (a, b) => dateText(a).localeCompare(dateText(b)),
          renderHeaderCell: () => MAIL_COLUMN_META.date.label,
          renderCell: (row) => (
            <CellText
              className={styles.cellText}
              text={row.kind === 'mail' ? formatMailDate(row.mail.receivedAt ?? row.mail.sentAt) : ''}
            />
          ),
        });
      case 'sentAt':
        return createTableColumn<Row>({
          columnId: 'sentAt',
          compare: (a, b) => sentText(a).localeCompare(sentText(b)),
          renderHeaderCell: () => MAIL_COLUMN_META.sentAt.label,
          renderCell: (row) => (
            <CellText
              className={styles.cellText}
              text={row.kind === 'mail' ? formatMailDate(row.mail.sentAt) : ''}
            />
          ),
        });
      case 'size':
        return createTableColumn<Row>({
          columnId: 'size',
          compare: (a, b) => sizeVal(a) - sizeVal(b),
          renderHeaderCell: () => MAIL_COLUMN_META.size.label,
          renderCell: (row) => (
            <CellText
              className={`${styles.cellText} ${styles.dim}`}
              text={row.kind === 'mail' ? humanFileSize(row.mail.sizeBytes) : ''}
            />
          ),
        });
      case 'filedBy':
        return createTableColumn<Row>({
          columnId: 'filedBy',
          compare: (a, b) => filedByText(a).localeCompare(filedByText(b)),
          renderHeaderCell: () => MAIL_COLUMN_META.filedBy.label,
          renderCell: (row) => (
            <CellText
              className={`${styles.cellText} ${styles.dim}`}
              text={row.kind === 'mail' ? (row.mail.filedByName ?? '') : ''}
            />
          ),
        });
      case 'filedAt':
        return createTableColumn<Row>({
          columnId: 'filedAt',
          compare: (a, b) => filedAtText(a).localeCompare(filedAtText(b)),
          renderHeaderCell: () => MAIL_COLUMN_META.filedAt.label,
          renderCell: (row) => (
            <CellText
              className={`${styles.cellText} ${styles.dim}`}
              text={row.kind === 'mail' ? formatMailDate(row.mail.filedAt) : ''}
            />
          ),
        });
    }
  };

  // Fixed anchor columns. renderCell for the actions column closes over the
  // current menu-item builders (folderMenuItems / mailMenuItems), which change
  // every render — so `columns` is rebuilt each render. Fluent keys sort +
  // column-sizing state by columnId (not array identity), so that's safe; only
  // `columnSizingOptions` below must stay referentially stable.
  const nameColumn = createTableColumn<Row>({
    columnId: 'name',
    compare: (a, b) => {
      // Folders sort before mails, then alphabetically.
      if (a.kind !== b.kind) return a.kind === 'folder' ? -1 : 1;
      const an = a.kind === 'folder' ? a.folder.name : mailLabel(a.mail);
      const bn = b.kind === 'folder' ? b.folder.name : mailLabel(b.mail);
      return an.localeCompare(bn);
    },
    renderHeaderCell: () => 'Nom / Objet',
    renderCell: (row) => (
      <div className={styles.cell}>
        {/* `isRead === false` STRICTEMENT : `undefined` signifie « back-end pas
            encore à jour » (migration 0027), et le traiter comme non-lu
            barbouillerait la grille entière de bleu sur un simple retard de
            déploiement. */}
        <UnreadBadge unread={row.kind === 'mail' && row.mail.isRead === false}>
          {row.kind === 'folder' ? (
            <FolderItemIcon className={styles.icon} />
          ) : downloadingIds.has(row.mail.id) ? (
            <Tooltip content="Téléchargement en cours…" relationship="label">
              <Spinner size="tiny" />
            </Tooltip>
          ) : (
            <MailItemIcon className={styles.icon} />
          )}
        </UnreadBadge>
        <CellText
          className={mergeClasses(
            styles.cellText,
            row.kind === 'mail' && row.mail.isRead === false && styles.unreadText,
            // Le fondu n'est armé QUE si le bouton va réellement paraître : sans
            // sélection Outlook, ou sur une ligne de mail, le nom ne doit pas
            // s'effacer au survol pour un bouton qui n'existe pas.
            row.kind === 'folder' && selection.length > 0 && 'fd-name-fade',
            row.kind === 'folder' && selection.length > 0 && styles.nameGrow,
          )}
          text={row.kind === 'folder' ? row.folder.name : mailLabel(row.mail)}
        />
        {/* Rangement en UN CLIC dans ce dossier, révélé au survol de la ligne
            (mêmes icône, libellé et infobulle que dans l'arbre). Il vivait dans la
            colonne d'actions, à l'autre bout de la ligne ; ici il touche le nom du
            dossier qu'il vise. Le dévoilement est purement CSS (`:hover` de la
            ligne), sans état React par ligne — la grille en compte des centaines. */}
        {row.kind === 'folder' && selection.length > 0 && (
          // `title` absent volontairement : l'infobulle NATIVE du navigateur se
          // superposerait à celle de Fluent, en doublon et sans le même contenu.
          // L'`aria-label` reste porté par le bouton — le contenu riche est du
          // JSX, donc inexploitable comme étiquette accessible.
          <FileHereTooltip items={selection}>
            <Button
              appearance="subtle"
              size="small"
              className={mergeClasses('fd-row-action', styles.rowHoverAction, styles.fileHereBtn)}
              icon={<MailArrowUp20Regular />}
              disabled={fileMailMut.isPending}
              aria-label={`Ranger ${selection.length} mail(s) dans ${row.folder.name}`}
              // Les deux clics doivent être arrêtés ICI : le bouton est désormais
              // DANS la cellule du nom, qui ne stoppe rien (contrairement à la
              // cellule d'actions d'où il vient). Sans cela, ranger déplacerait la
              // sélection de ligne, et deux clics rapides ouvriraient le dossier.
              onClick={(e) => {
                e.stopPropagation();
                fileSelectionInto(row.folder.id);
              }}
              onDoubleClick={(e) => e.stopPropagation()}
            />
          </FileHereTooltip>
        )}
        {row.kind === 'mail' && row.mail.hasAttachments && (
          <Tooltip content={attachmentTooltip(row.mail, styles)} relationship="label">
            {/* Le trombone est une icône, pas un contrôle : Fluent a besoin d'un
                élément qui accepte une ref, et le survol seul exclurait le
                clavier. D'où le span focusable — même arbitrage que pour les
                actions révélées au survol de la ligne. Les clics continuent de
                remonter à la ligne : le trombone fait partie de la ligne, il
                sélectionne donc au clic simple et ouvre au double, comme le reste
                de la cellule. */}
            <span className={styles.attachTrigger} tabIndex={0}>
              <Attach16Regular className={styles.icon} />
            </span>
          </Tooltip>
        )}
      </div>
    ),
  });

  const actionsColumn = createTableColumn<Row>({
    columnId: 'actions',
    // The empty actions-column header holds the column-settings gear.
    //
    // Poussé À DROITE, comme les ⋯ des lignes en dessous : la cellule d'en-tête
    // absorbe l'espace restant de la grille, donc sans `width: 100%` le div se
    // dimensionne sur son contenu et `flex-end` n'a rien à distribuer — le bouton
    // restait collé au bord gauche, à distance du bord du volet.
    renderHeaderCell: () => (
      <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%', minWidth: 0 }}>
        <ColumnSettingsPopover narrowHidden={narrowHidden} />
      </div>
    ),
    // The ⋯ button is its OWN menu (button = trigger) so keyboard focus
    // restores onto it on close. Right-click on the row opens the shared
    // cursor-positioned menu instead (same items).
    renderCell: (row) => (
      // Stop clicks in this cell from reaching the row's handlers (select on
      // click, OPEN on double-click). This wrapper is essential for the MENU ITEMS
      // too, not just the ⋯ button: Fluent portals the popover, and React
      // re-parents portal events to the <Menu>'s React ancestor — so a click on
      // "Copier"/"Couper"/… bubbles here (and is stopped) instead of moving the
      // selection or, deux clics rapprochés, d'ouvrir la ligne.
      //
      // `dblclick` doit être stoppé À PART : ce n'est pas un `click` et il ne
      // passe donc pas par le gestionnaire ci-dessus. Deux clics rapides sur ⋯,
      // sur « Ranger ici » ou sur deux entrées de menu à la suite en émettent un —
      // qui remonterait à la ligne et téléchargerait le mail. Même remède que
      // MailRow, FolderRow et ContainerSection dans l'arbre. `pointerdown` n'a en
      // revanche pas à l'être ici : la ligne n'y réagit pas, et le couper
      // priverait Fluent de l'événement dont il se sert pour refermer les
      // surfaces ouvertes ailleurs.
      <div
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          // `width: 100%` est INDISPENSABLE, pas cosmétique : sans elle ce div se
          // dimensionne sur son contenu, si bien que `justifyContent: flex-end`
          // n'a aucun espace libre à distribuer et ne déplace RIEN — les boutons
          // se collaient au bord GAUCHE de la cellule d'actions, qui absorbe
          // pourtant l'espace restant de la grille. Mesuré : ⋯ à droite=719 dans
          // une cellule allant jusqu'à 893.
          width: '100%',
          minWidth: 0,
        }}
      >
        {/* « Ranger ici » n'est plus ici : il a rejoint l'extrémité droite de la
            colonne « Nom / Objet », au bout du nom du dossier qu'il vise (cf.
            nameColumn). Cette cellule ne porte donc plus qu'un seul bouton, et
            l'alignement des ⋯ entre lignes dossier et lignes mail ne dépend plus
            de leur nombre. */}
        <Menu positioning="below-end">
          <MenuTrigger disableButtonEnhancement>
            <Button
              appearance="subtle"
              size="small"
              // Révélé au survol de la ligne, comme « Ranger ici » dans la colonne
              // du nom : un ⋯ sur chaque ligne en permanence chargeait la liste
              // d'une colonne de points qui ne dit rien du contenu.
              className={mergeClasses('fd-row-action', styles.rowHoverAction)}
              icon={<MoreHorizontalRegular />}
              aria-label="Actions"
            />
          </MenuTrigger>
          <MenuPopover>
            <MenuList>
              {row.kind === 'folder' ? folderMenuItems(row.folder) : mailMenuItems(row.mail)}
            </MenuList>
          </MenuPopover>
        </Menu>
      </div>
    ),
  });

  const columns: TableColumnDefinition<Row>[] = [
    nameColumn,
    // `fittedMiddle` et non `visibleMiddle` : une colonne que le volet ne peut pas
    // porter n'est pas rendue du tout. La retirer ICI plutôt que de la laisser à
    // largeur nulle évite d'avoir à traiter un cas de largeur zéro dans toute la
    // chaîne (tri, redimensionnement, glisser d'en-tête).
    ...fittedMiddle.map(buildMiddleColumn),
    actionsColumn,
  ];

  // Column widths fill the pane proportionally: `name` + the fitted middle
  // columns share the available width (pane minus the fixed actions column) by
  // weight = their natural width, each clamped to its own minimum; the rounding
  // remainder goes to `name` so the total exactly equals the pane and the
  // actions column stays flush against the right edge. Toggling a column changes
  // `fittedKey`, so the rest reflow proportionally. Recomputed only on
  // width/visible-set change — otherwise a stable reference, since Fluent resets
  // column widths whenever this object's identity changes.
  //
  // Le partage lui-même est dans `layoutFlexColumns`, PARTAGÉ avec le choix des
  // colonnes retenues : c'est ce qui garantit que le seuil qui fait tomber une
  // colonne et la largeur finalement obtenue parlent de la même chose.
  const sizing = useMemo<TableColumnSizingOptions>(() => {
    const flexIds: string[] = ['name', ...fittedMiddle];
    const opts: TableColumnSizingOptions = {
      actions: { minWidth: ACTIONS_COL_WIDTH, defaultWidth: ACTIONS_COL_WIDTH, idealWidth: ACTIONS_COL_WIDTH },
    };
    // Budget de CONTENU : Fluent ajoute son rembourrage à chacune des largeurs
    // qu'on lui donne (cf. contentBudget). Le même budget que celui sur lequel
    // `fitMiddleColumns` a tranché — c'est tout l'intérêt de les partager.
    const avail = gridWidth > 0 ? contentBudget(gridWidth, fittedMiddle.length) : 0;

    // Before the first measurement, fall back to natural widths so the first
    // paint is sane; the ResizeObserver then drives the proportional pass.
    if (avail <= 0) {
      for (const id of flexIds) {
        const natural = id === 'name' ? NAME_COL_WEIGHT : MAIL_COLUMN_META[id as MailColumnId].defaultWidth;
        opts[id] = { minWidth: columnMinWidth(id), defaultWidth: natural, idealWidth: natural };
      }
      return opts;
    }

    const widths = layoutFlexColumns(avail, fittedMiddle);
    for (const id of flexIds) {
      const w = widths[id] ?? columnMinWidth(id);
      opts[id] = { minWidth: columnMinWidth(id), defaultWidth: w, idealWidth: w };
    }
    return opts;
    // fittedKey encodes fittedMiddle; recompute on pane resize or toggle only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridWidth, fittedKey]);

  const loading = foldersLoading || (mailsEnabled && mailsLoading);

  return (
    <div className={styles.root}>
      <div className={styles.bar}>
        <Tooltip content="Accueil (arborescence)" relationship="label">
          <Button appearance="subtle" size="small" icon={<Home20Regular />} onClick={backToTree} />
        </Tooltip>
        <Tooltip content="Précédent" relationship="label">
          <Button
            appearance="subtle"
            size="small"
            icon={<ChevronLeftRegular />}
            disabled={!canGoBack}
            onClick={goBack}
            aria-label="Précédent"
          />
        </Tooltip>
        <Tooltip content="Suivant" relationship="label">
          <Button
            appearance="subtle"
            size="small"
            icon={<ChevronRightRegular />}
            disabled={!canGoForward}
            onClick={goForward}
            aria-label="Suivant"
          />
        </Tooltip>
        <div className={styles.crumbs}>
          <FolderBreadcrumb crumbs={crumbs} />
        </div>
      </div>

      <div className={styles.body} onContextMenu={onEmptyContext}>
        {loading ? (
          <div className={styles.empty}>
            <Spinner size="tiny" label="Chargement…" />
          </div>
        ) : rows.length === 0 ? (
          <div className={styles.emptyState}>
            <FolderRegular className={styles.emptyIcon} />
            <Text className={styles.emptyTitle}>Ce dossier est vide</Text>
            <Text className={styles.emptySub}>
              Créez un sous-dossier, ou rangez-y les mails sélectionnés dans Outlook.
            </Text>
            <div className={styles.emptyActions}>
              <Button
                appearance="primary"
                icon={<FolderAdd20Regular />}
                onClick={() => startCreate(detailFolderId)}
              >
                Nouveau dossier
              </Button>
              {detailFolderId && (
                <Tooltip
                  content={
                    selection.length === 0
                      ? 'Sélectionnez d’abord un ou plusieurs mails dans Outlook'
                      : `Ranger ${selection.length} mail(s) dans ce dossier`
                  }
                  relationship="label"
                >
                  <Button
                    appearance="secondary"
                    icon={<MailArrowUp20Regular />}
                    disabled={selection.length === 0 || fileMailMut.isPending}
                    onClick={() => detailFolderId && fileSelectionInto(detailFolderId)}
                  >
                    {fileMailMut.isPending
                      ? 'Rangement…'
                      : selection.length > 0
                        ? `Ranger ${selection.length} mail(s) ici`
                        : 'Ranger les mails sélectionnés'}
                  </Button>
                </Tooltip>
              )}
            </div>
          </div>
        ) : (
          // Glisser-déposer INTERNE à la page : traîner un mail ou un dossier sur
          // la ligne d'un autre dossier l'y déplace. Les règles (qui peut être
          // déposé où, quelles requêtes invalider, quel refus dire) sont celles de
          // l'arbre, littéralement — même composant.
          //
          // Cohabite avec le glisser venu d'OUTLOOK, qui est un tout autre chemin :
          // celui-là passe par un événement Office et le repérage
          // `data-drop-folder-id`, sans jamais toucher dnd-kit. Les deux affichent
          // la même surbrillance sur la ligne visée.
          <ItemDndContext>
            <div className={styles.grid} ref={gridRef}>
              <DataGrid
                items={rows}
                columns={columns}
                sortable
                resizableColumns
                columnSizingOptions={sizing}
                getRowId={(row: Row) => row.id}
                // Densité « small » : cellules de 34px au lieu de 44px (~30 % de
                // lignes visibles en plus dans un volet qui est toujours court),
                // en gardant la taille de police. `size` s'applique à TOUS les
                // sous-composants du tableau, donc l'en-tête se compacte aussi.
                // L'échelon suivant (`extra-small`, 24px) rognerait la police à
                // 12px et laisserait moins de place que la hauteur du bouton ⋯.
                size="small"
              >
                <DataGridHeader>
                  <DataGridRow>
                    {({ renderHeaderCell, columnId }) => (
                      <DataGridHeaderCell>
                        {isReorderableColumn(columnId) ? (
                          <ReorderableHeader columnId={columnId}>
                            {renderHeaderCell()}
                          </ReorderableHeader>
                        ) : (
                          renderHeaderCell()
                        )}
                      </DataGridHeaderCell>
                    )}
                  </DataGridRow>
                </DataGridHeader>
                <DataGridBody<Row>>
                  {({ item, rowId }) => (
                    <DetailGridRow
                      key={rowId}
                      item={item}
                      selected={sel.isSelected(refOfRow(item).id)}
                      officeDropOver={
                        item.kind === 'folder' &&
                        dropTarget?.kind === 'folder' &&
                        dropTarget.folderId === item.folder.id
                      }
                      attrs={sel.attrs(refOfRow(item))}
                      onSelect={(e) => {
                        const ref = refOfRow(item);
                        // CTRL/MAJ d'abord ; un clic simple retombe sur une
                        // sélection unique, comme avant.
                        if (sel.onRowClick(e, ref)) return;
                        sel.selectOnly(ref);
                      }}
                      onOpen={() => openRow(item)}
                      onContext={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        // Un clic droit sélectionne aussi la ligne, comme dans
                        // l'Explorateur : le menu qui s'ouvre agit sur ELLE, et
                        // rien d'autre ne le montrerait dans une liste dense.
                        // MAIS il ne casse PAS une multi-sélection dont la ligne
                        // fait partie : ce serait ramener quatre éléments visés à
                        // un seul, juste avant d'ouvrir le menu qui agit dessus.
                        const ref = refOfRow(item);
                        if (!sel.isSelected(ref.id)) sel.selectOnly(ref);
                        openRowMenuAt(e.clientX, e.clientY, item);
                      }}
                    />
                  )}
                </DataGridBody>
              </DataGrid>
            </div>
          </ItemDndContext>
        )}
      </div>

      {/* Shared context menu — its contents depend on what was clicked. Anchored
          to the cursor (or the ⋯ button) via a virtual positioning target. */}
      <Menu
        open={ctx !== null}
        onOpenChange={(_e, d) => {
          if (!d.open) closeCtx();
        }}
        positioning={{ target: ctx?.target, position: 'below', align: 'start' }}
      >
        <MenuTrigger disableButtonEnhancement>
          {/* Invisible anchor for the right-click menu. tabIndex=-1 keeps it out
              of the Tab order but lets Fluent's on-close focus-restore land on a
              real (focusable) element instead of dropping focus to <body>. It's
              1px + pointer-events:none so it never intercepts the drag hit-test
              or clicks. (The ⋯ button path uses its own button-anchored menu.) */}
          <span
            tabIndex={-1}
            aria-hidden
            style={{ position: 'fixed', width: 1, height: 1, padding: 0, opacity: 0, pointerEvents: 'none' }}
          />
        </MenuTrigger>
        <MenuPopover>
          <MenuList>
            {/* MENU DE FOND (clic droit hors ligne) : il agit sur le dossier
                AFFICHÉ, pas sur une ligne. Même grammaire que les autres menus —
                ranger la sélection d'abord, puis créer, puis le presse-papiers.
                « Ranger ici » y manquait : l'entrée n'existait que sur une ligne
                de sous-dossier, donc ranger dans le dossier ouvert obligeait à
                remonter d'un niveau pour viser sa ligne. Le raccourci de l'état
                vide fait déjà exactement cela, mais disparaît dès que le dossier
                contient quelque chose. */}
            {ctx !== null && ctxRow === null && detailFolderId && selection.length > 0 && (
              <>
                <MenuItem
                  icon={<MailArrowUp20Regular />}
                  onClick={() => {
                    closeCtx();
                    if (detailFolderId) fileSelectionInto(detailFolderId);
                  }}
                >
                  Ranger {selection.length} mail(s) ici
                </MenuItem>
                <MenuDivider />
              </>
            )}
            {ctx !== null && ctxRow === null && (
              <MenuItem icon={<FolderAdd20Regular />} onClick={() => startCreate(detailFolderId)}>
                Nouveau dossier
              </MenuItem>
            )}
            {ctx !== null && ctxRow === null && detailFolderId && (
              <>
                <MenuDivider />
                <MenuItem
                  icon={<ClipboardPasteRegular />}
                  disabled={!canPaste || isBusy}
                  title={pasteHint}
                  onClick={() => {
                    closeCtx();
                    // Le CONTENEUR de la page doit être passé, comme aux quatre
                    // autres points d'appel : sans lui, coller un dossier venu d'un
                    // AUTRE espace retombait sur le conteneur de la source, et le
                    // serveur refusait en 400 (« le dossier de destination
                    // appartient à un autre espace »).
                    if (detailFolderId) pasteInto(detailFolderId, containerId ?? undefined);
                  }}
                >
                  Coller
                </MenuItem>
              </>
            )}
            {/* MENU DE LOT dès que la ligne visée fait partie d'une
                multi-sélection : les actions unitaires seraient trompeuses, elles
                n'agiraient que sur une des lignes désignées. */}
            {ctxRow !== null && multiMenu.isMulti && sel.isSelected(refOfRow(ctxRow).id)
              ? multiMenu.items
              : (
                  <>
                    {ctxRow?.kind === 'folder' && folderMenuItems(ctxRow.folder)}
                    {ctxRow?.kind === 'mail' && mailMenuItems(ctxRow.mail)}
                  </>
                )}
          </MenuList>
        </MenuPopover>
      </Menu>

      {/* Dialogues des actions de lot (déplacer, supprimer). Hors du `MenuList`,
          qui est démonté avec le menu dès le premier clic. */}
      {multiMenu.dialogs}

      {/* Create-folder dialog (empty-area "Nouveau dossier" or a folder's
          "Nouveau sous-dossier"). */}
      <Dialog
        open={createParentId !== undefined}
        onOpenChange={(_e, d) => !d.open && setCreateParentId(undefined)}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Nouveau dossier</DialogTitle>
            <DialogContent>
              <Input
                value={createName}
                autoFocus
                style={{ width: '100%' }}
                placeholder="Nom du dossier"
                disabled={createFolderMut.isPending}
                onChange={(_e, d) => setCreateName(d.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') submitCreate();
                }}
              />
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setCreateParentId(undefined)}>
                Annuler
              </Button>
              <Button
                appearance="primary"
                disabled={createFolderMut.isPending || !createName.trim()}
                onClick={submitCreate}
              >
                {createFolderMut.isPending ? 'Création…' : 'Créer'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Mail rename */}
      <Dialog open={mailRename !== null} onOpenChange={(_e, d) => !d.open && setMailRename(null)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Renommer le mail</DialogTitle>
            <DialogContent>
              <Input
                value={mailRenameVal}
                autoFocus
                style={{ width: '100%' }}
                placeholder="Nom d'affichage (vide = objet d'origine)"
                disabled={renameMail.isPending}
                onChange={(_e, d) => setMailRenameVal(d.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') submitMailRename();
                }}
              />
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setMailRename(null)}>
                Annuler
              </Button>
              <Button appearance="primary" disabled={renameMail.isPending} onClick={submitMailRename}>
                {renameMail.isPending ? 'Renommage…' : 'Renommer'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Mail delete */}
      <Dialog open={mailDelete !== null} onOpenChange={(_e, d) => !d.open && setMailDelete(null)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Supprimer le mail ?</DialogTitle>
            <DialogContent>
              {mailDelete && `« ${mailLabel(mailDelete)} » sera supprimé (récupération possible pendant 30 jours).`}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setMailDelete(null)}>
                Annuler
              </Button>
              <Button
                appearance="primary"
                disabled={delMail.isPending}
                onClick={() =>
                  mailDelete &&
                  delMail.mutate(
                    { mailId: mailDelete.id, folderId: mailDelete.folderId },
                    { onSettled: () => setMailDelete(null) },
                  )
                }
              >
                {delMail.isPending ? 'Suppression…' : 'Supprimer'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Folder rename */}
      <Dialog open={folderRename !== null} onOpenChange={(_e, d) => !d.open && setFolderRename(null)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Renommer le dossier</DialogTitle>
            <DialogContent>
              <Input
                value={folderRenameVal}
                autoFocus
                style={{ width: '100%' }}
                placeholder="Nom du dossier"
                disabled={renameFolderMut.isPending}
                onChange={(_e, d) => setFolderRenameVal(d.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') submitFolderRename();
                }}
              />
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setFolderRename(null)}>
                Annuler
              </Button>
              <Button appearance="primary" disabled={renameFolderMut.isPending} onClick={submitFolderRename}>
                {renameFolderMut.isPending ? 'Renommage…' : 'Renommer'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Folder delete */}
      <Dialog open={folderDelete !== null} onOpenChange={(_e, d) => !d.open && setFolderDelete(null)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Supprimer le dossier ?</DialogTitle>
            <DialogContent>
              {folderDelete &&
                `« ${folderDelete.name} » sera supprimé. Les mails et sous-dossiers à l'intérieur seront masqués (récupération possible pendant 30 jours via la corbeille).`}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setFolderDelete(null)}>
                Annuler
              </Button>
              <Button
                appearance="primary"
                disabled={deleteFolderMut.isPending}
                onClick={() =>
                  folderDelete &&
                  deleteFolderMut.mutate(
                    { folderId: folderDelete.id },
                    { onSettled: () => setFolderDelete(null) },
                  )
                }
              >
                {deleteFolderMut.isPending ? 'Suppression…' : 'Supprimer'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {mailTarget && (
        <MailTargetDialog mode={mailTarget.mode} mail={mailTarget.mail} onClose={() => setMailTarget(null)} />
      )}
      {folderTarget && (
        <MoveFolderDialog
          open
          mode={folderTarget.mode}
          folder={folderTarget.folder}
          onClose={() => setFolderTarget(null)}
        />
      )}
    </div>
  );
}
